import {
  AloftArea,
  AloftPrice,
  Improvements,
  ORDER_FEATURES,
  OrderFeature,
  PROPERTY_GRID_SUBJECT_PROPERTY,
  getAbbreviatedCarportGarage,
  isDefined,
  orUndefined,
} from '@aloft/toolkit-shared';

import { cloneDeep } from 'lodash';

import { PropertyGridSection } from '../../../components/PropertyGrid/columnSections/PropertyGridSection.tsx';
import { PropertyGridSectionProps } from '../../../components/PropertyGrid/types.ts';
import { selectSalesComparisonApproach } from '../../../redux/features/selectors/orderSelectors.ts';
import { selectImprovementsEntities } from '../../../redux/features/selectors/propertyDataSelectors/improvementsSelectors.ts';
import { selectReconciliation } from '../../../redux/features/selectors/selectReconciliation.ts';
import { useAppSelector } from '../../../redux/hooks.ts';
import { ADJUSTMENT_LABELS } from '../../adjustments/internal/PairedCostSettings/internal/consts.ts';
import { ImprovementField, improvementsFieldName } from './consts.ts';

export const AdjustmentsGridAdditionalFeaturesSection = (props: PropertyGridSectionProps) => {
  const improvementEntities = useAppSelector(selectImprovementsEntities);
  const { includeTotalCostToCure, totalCostToCure } = useAppSelector(selectReconciliation);
  const isUnitRate = useAppSelector(selectSalesComparisonApproach) === 'unit_rate';

  const featureFields: ImprovementField[] = [];

  const belowGradeHalfBathField: ImprovementField = {
    fieldName: improvementsFieldName,
    label: ADJUSTMENT_LABELS.basementHalfBaths,
    manualAdjustmentKey: 'basementHalfBaths',
    unitRate: {
      key: 'basementHalfBaths',
      label: '/ba',
    },
    toDisplayValue: (improvements) =>
      isDefined(improvements?.basement?.halfBaths)
        ? `${improvements?.basement.halfBaths ?? 0}`
        : undefined,
  };

  const belowGradeFullBathField: ImprovementField = {
    fieldName: improvementsFieldName,
    label: ADJUSTMENT_LABELS.basementFullBaths,
    manualAdjustmentKey: 'basementFullBaths',
    unitRate: {
      key: 'basementFullBaths',
      label: '/ba',
    },
    toDisplayValue: (improvements) =>
      isDefined(improvements?.basement?.fullBaths)
        ? `${improvements?.basement.fullBaths ?? 0}`
        : undefined,
  };

  const bathBelowGradeField: ImprovementField = {
    fieldName: improvementsFieldName,
    label: ADJUSTMENT_LABELS.bathBelowGrade,
    manualAdjustmentKey: 'bathBelowGrade',
    toDisplayValue: (improvements) => {
      if (
        !isDefined(improvements?.basement?.fullBaths) &&
        !isDefined(improvements?.basement?.halfBaths)
      ) {
        return undefined;
      }

      return `${improvements?.basement?.fullBaths ?? 0}.${improvements?.basement?.halfBaths ?? 0}`;
    },
  };

  const garageCarportField: ImprovementField = {
    fieldName: improvementsFieldName,
    label: ADJUSTMENT_LABELS.garageCarport,
    manualAdjustmentKey: 'garageCarport',
    unitRate: {
      key: 'garageCarport',
      label: '/spc',
    },
    toDisplayValue: (improvements) => {
      if (!improvements) return undefined;
      const value = getAbbreviatedCarportGarage(improvements);
      if (value) return value;
      const garageFeatures = (['carport', 'detachedGarage', 'garage'] as const).filter((f) =>
        ORDER_FEATURES[f].isNone(improvements),
      );
      return garageFeatures.length > 0 ? 'None' : undefined;
    },
  };

  const porchPatioDeckField: ImprovementField = {
    fieldName: improvementsFieldName,
    label: ADJUSTMENT_LABELS.porchPatioDeck,
    manualAdjustmentKey: 'porchPatioDeck',
    toDisplayValue: (improvements) => {
      if (!improvements) return undefined;

      const presentFeatures = [ORDER_FEATURES.porch, ORDER_FEATURES.patio, ORDER_FEATURES.deck]
        .filter((feature) => feature.hasFeature(improvements))
        .map((feature) => feature.label)
        .join(';');

      if (presentFeatures) return presentFeatures;

      const hasAnyNone = [ORDER_FEATURES.porch, ORDER_FEATURES.patio, ORDER_FEATURES.deck].some(
        (feature) => feature.isNone(improvements),
      );
      return hasAnyNone ? 'None' : undefined;
    },
  };

  const atticField: ImprovementField = {
    fieldName: improvementsFieldName,
    label: ADJUSTMENT_LABELS.attic,
    manualAdjustmentKey: 'attic',
    toDisplayValue: (improvements) => {
      if (!improvements) return undefined;
      return (
        orUndefined(ORDER_FEATURES.attic.displayValue)(improvements as Improvements) ??
        (ORDER_FEATURES.attic.isNone(improvements as Improvements) ? 'None' : undefined)
      );
    },
  };

  const fireplaceField: ImprovementField = {
    fieldName: improvementsFieldName,
    label: ADJUSTMENT_LABELS.fireplace,
    manualAdjustmentKey: 'fireplace',
    toDisplayValue: (improvements) => {
      if (!improvements) return undefined;
      return (
        orUndefined(ORDER_FEATURES.fireplace.displayValue)(improvements as Improvements) ??
        (ORDER_FEATURES.fireplace.isNone(improvements as Improvements) ? 'None' : undefined)
      );
    },
  };

  const barnField: ImprovementField = {
    fieldName: improvementsFieldName,
    label: ADJUSTMENT_LABELS.barn,
    manualAdjustmentKey: 'barn',
    toDisplayValue: (improvements) => {
      if (!improvements) return undefined;
      return (
        orUndefined(ORDER_FEATURES.barn.displayValue)(improvements as Improvements) ??
        (ORDER_FEATURES.barn.isNone(improvements as Improvements) ? 'None' : undefined)
      );
    },
  };

  const aduField: ImprovementField = {
    fieldName: improvementsFieldName,
    label: ADJUSTMENT_LABELS.adu,
    manualAdjustmentKey: 'adu',
    unitRate: {
      key: 'adu',
      label: '/sf',
    },
    toDisplayValue: (improvements) => {
      if (!improvements) return undefined;
      return (
        orUndefined(ORDER_FEATURES.adu.displayValue)(improvements as Improvements) ??
        (ORDER_FEATURES.adu.isNone(improvements as Improvements) ? 'None' : undefined)
      );
    },
  };

  const basementTotalField: ImprovementField = {
    fieldName: improvementsFieldName,
    label: ADJUSTMENT_LABELS.basementTotal,
    manualAdjustmentKey: 'basementTotal',
    unitRate: {
      key: 'basementTotal',
      label: '/sf',
    },
    toDisplayValue: (improvements) => {
      if (!improvements?.basement) return undefined;

      const { totalArea } = improvements.basement;
      if (!totalArea || !ORDER_FEATURES.basement.hasFeature(improvements)) return undefined;

      return AloftArea.schema.parse(totalArea).getSiteAreaString('');
    },
  };

  const basementFinishedField: ImprovementField = {
    fieldName: improvementsFieldName,
    label: ADJUSTMENT_LABELS.basementFinished,
    manualAdjustmentKey: 'basementFinished',
    unitRate: {
      key: 'basementFinished',
      label: '/sf',
    },
    toDisplayValue: (improvements) => {
      if (!improvements?.basement) return undefined;

      const { finishedArea } = improvements.basement;
      if (!finishedArea || !ORDER_FEATURES.basement.hasFeature(improvements)) return undefined;

      return AloftArea.schema.parse(finishedArea).getSiteAreaString('');
    },
  };

  const basementField: ImprovementField = {
    fieldName: improvementsFieldName,
    label: ADJUSTMENT_LABELS.basement,
    manualAdjustmentKey: 'basement',
    toDisplayValue: (improvements) => {
      if (!improvements) return undefined;
      return (
        orUndefined(ORDER_FEATURES.basement.displayValue)(improvements) ??
        (ORDER_FEATURES.basement.isNone(improvements as Improvements) ? 'None' : undefined)
      );
    },
  };

  const poolSpaField: ImprovementField = {
    fieldName: improvementsFieldName,
    label: ADJUSTMENT_LABELS.poolSpa,
    manualAdjustmentKey: 'poolSpa',
    toDisplayValue: (improvements) => {
      if (!improvements) return undefined;

      const poolDisplay = orUndefined(ORDER_FEATURES.pool.displayValue)(improvements);
      const spaDisplay = orUndefined(ORDER_FEATURES.spa.displayValue)(improvements);

      const parts = [
        poolDisplay ? `${poolDisplay} Pool` : undefined,
        spaDisplay ? `${spaDisplay} Spa` : undefined,
      ].filter(isDefined);

      if (parts.length > 0) return parts.join('; ');

      const hasAnyNone =
        ORDER_FEATURES.pool.isNone(improvements) || ORDER_FEATURES.spa.isNone(improvements);
      return hasAnyNone ? 'None' : undefined;
    },
  };

  const shedField: ImprovementField = {
    fieldName: improvementsFieldName,
    label: ADJUSTMENT_LABELS.shed,
    manualAdjustmentKey: 'shed',
    toDisplayValue: (improvements) => {
      if (!improvements) return undefined;
      return (
        orUndefined(ORDER_FEATURES.shed.displayValue)(improvements) ??
        (ORDER_FEATURES.shed.isNone(improvements as Improvements) ? 'None' : undefined)
      );
    },
  };
  const workshopField: ImprovementField = {
    fieldName: improvementsFieldName,
    label: ADJUSTMENT_LABELS.workshop,
    manualAdjustmentKey: 'workshop',
    toDisplayValue: (improvements) => {
      if (!improvements) return undefined;
      return (
        orUndefined(ORDER_FEATURES.workshop.displayValue)(improvements) ??
        (ORDER_FEATURES.workshop.isNone(improvements as Improvements) ? 'None' : undefined)
      );
    },
  };

  const manualCostField: ImprovementField = {
    fieldName: improvementsFieldName,
    label: ADJUSTMENT_LABELS.manual,
    manualAdjustmentKey: 'manual',
    toDisplayValue: (improvements) =>
      orUndefined(ORDER_FEATURES.manualCost.displayValue)(improvements),
  };

  const shouldDisplayFeatureField = (featureDependencies: OrderFeature[]) =>
    featureDependencies.some((feature) =>
      Object.values(improvementEntities).some(
        (improvements) =>
          ORDER_FEATURES[feature].hasFeature(improvements) ||
          ORDER_FEATURES[feature].isNone(improvements),
      ),
    );

  if (shouldDisplayFeatureField(['carport', 'detachedGarage', 'garage'])) {
    featureFields.push(garageCarportField);
  }
  if (shouldDisplayFeatureField(['porch', 'patio', 'deck'])) {
    featureFields.push(porchPatioDeckField);
  }
  if (shouldDisplayFeatureField(['attic'])) featureFields.push(atticField);
  if (shouldDisplayFeatureField(['fireplace'])) featureFields.push(fireplaceField);
  if (shouldDisplayFeatureField(['barn'])) featureFields.push(barnField);
  if (shouldDisplayFeatureField(['adu'])) featureFields.push(aduField);
  if (shouldDisplayFeatureField(['basement'])) {
    const basements = Object.values(improvementEntities)
      .filter((improvements) => ORDER_FEATURES.basement.hasFeature(improvements as Improvements))
      .map(({ basement }) => basement!);

    let basementFieldsAdded = 0;

    if (
      isUnitRate &&
      basements.some(({ totalArea }) => isDefined(totalArea) && totalArea.unitAmount > 0)
    ) {
      featureFields.push(basementTotalField);
      basementFieldsAdded++;
    }

    if (
      isUnitRate &&
      basements.some(({ finishedArea }) => isDefined(finishedArea) && finishedArea.unitAmount > 0)
    ) {
      featureFields.push(basementFinishedField);
      basementFieldsAdded++;
    }

    if (
      !isUnitRate &&
      basements.some(
        ({ totalArea, finishedArea }) =>
          (isDefined(totalArea) && totalArea.unitAmount > 0) ||
          (isDefined(finishedArea) && finishedArea.unitAmount > 0),
      )
    ) {
      featureFields.push(basementField);
      basementFieldsAdded++;
    }

    if (isUnitRate && basements.some(({ fullBaths }) => isDefined(fullBaths) && fullBaths > 0)) {
      featureFields.push(belowGradeFullBathField);
      basementFieldsAdded++;
    }

    if (isUnitRate && basements.some(({ halfBaths }) => isDefined(halfBaths) && halfBaths > 0)) {
      featureFields.push(belowGradeHalfBathField);
      basementFieldsAdded++;
    }

    if (
      !isUnitRate &&
      basements.some(
        ({ fullBaths, halfBaths }) =>
          (isDefined(fullBaths) && fullBaths > 0) || (isDefined(halfBaths) && halfBaths > 0),
      )
    ) {
      featureFields.push(bathBelowGradeField);
      basementFieldsAdded++;
    }

    // If no data sub-fields were added but some property has basement explicitly set to None,
    // show a single Basement row with 'None'.
    if (basementFieldsAdded === 0) {
      featureFields.push(basementField);
    }
  }
  if (shouldDisplayFeatureField(['pool', 'spa'])) featureFields.push(poolSpaField);
  if (shouldDisplayFeatureField(['shed'])) featureFields.push(shedField);
  if (shouldDisplayFeatureField(['workshop'])) featureFields.push(workshopField);
  if (shouldDisplayFeatureField(['manualCost'])) featureFields.push(manualCostField);

  const totalCostToCureField: ImprovementField = {
    fieldName: improvementsFieldName,
    label: ADJUSTMENT_LABELS.totalCostToCure,
    manualAdjustmentKey: 'totalCostToCure',
    tooltipContent:
      'Major items only (i.e. soundness, security, livability). No cosmetic or minor deferred maintenance.',
    toDisplayValue: (improvements, propertyId) => {
      if (propertyId !== PROPERTY_GRID_SUBJECT_PROPERTY) {
        return improvements?.totalCostToCureDisplayName;
      }

      return orUndefined(AloftPrice.schema.parse)(totalCostToCure)?.toString(true);
    },
    toEditValue: (improvements, propertyId) => {
      if (propertyId !== PROPERTY_GRID_SUBJECT_PROPERTY) {
        return improvements?.totalCostToCureDisplayName;
      }

      return orUndefined(AloftPrice.schema.parse)(totalCostToCure)?.toDollarLike().toString();
    },
    toValue: (value: string | undefined, propertyId: string) => {
      const improvements = cloneDeep(improvementEntities[propertyId]);
      if (!improvements) return undefined;

      if (propertyId !== PROPERTY_GRID_SUBJECT_PROPERTY) {
        improvements.totalCostToCureDisplayName = value;
      }

      return improvements;
    },
  };

  const otherField: ImprovementField = {
    fieldName: improvementsFieldName,
    label: ADJUSTMENT_LABELS.other,
    manualAdjustmentKey: 'other',
    toDisplayValue: (improvements) => improvements?.otherAdjustmentsDisplayName,
    toEditValue: (improvements) => improvements?.otherAdjustmentsDisplayName,
    toValue: (value: string | undefined, propertyId: string) => {
      const improvements = cloneDeep(improvementEntities[propertyId]);
      if (!improvements) return undefined;

      improvements.otherAdjustmentsDisplayName = value;
      return improvements;
    },
  };

  return (
    <PropertyGridSection
      {...props}
      title="Additional Features"
      hasSeparateArvValue
      fields={[
        ...featureFields,
        ...(includeTotalCostToCure ? [totalCostToCureField] : []),
        otherField,
      ]}
    />
  );
};
